import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type {
  FreezeWindowSettings,
  NowPlayingResponse,
  RuntimeStatusEntry,
  SchedulingSettings,
} from '@shiocast/contracts';
import { setStationTimeZone } from '@shiocast/ui';
import { request } from '@/api/client';
import { HEALTH_EVENTS, ON_AIR_EVENTS, RealtimeChannel } from '@/api/realtime';

/**
 * How often the panel refreshes on-air state while no realtime channel is attached.
 *
 * This is the fallback cadence, not the normal one. With the socket up, a track change
 * shows in about a hundred milliseconds; these timers exist for the case where the
 * socket cannot be established at all — a proxy that will not upgrade, a captive
 * network — because a slower panel is acceptable and a wrong one is not.
 */
const POLL_MS = 10_000;
/** Health changes more slowly than what is playing. */
const HEALTH_POLL_MS = 30_000;
/**
 * While the socket is up the timers still run, far enough apart to be a safety net
 * rather than a poll: it catches anything an event did not cover without asking the API
 * a question every ten seconds for every open tab.
 */
const IDLE_POLL_MS = 120_000;

export const useStationStore = defineStore('station', () => {
  const nowPlaying = ref<NowPlayingResponse | null>(null);
  const health = ref<RuntimeStatusEntry[]>([]);
  const lastError = ref<string | null>(null);
  /** Set when the API cannot be reached, so the panel says so rather than showing stale state as current. */
  const stale = ref(false);
  /** Set when the last health read failed, so the grid is shown as last-known rather than current. */
  const healthStale = ref(false);

  /**
   * Station policy the panel needs to render correctly.
   *
   * The timezone matters because a scheduler drawn in the operator's laptop timezone is
   * wrong for anyone travelling, and the freeze window decides whether a move needs a
   * recorded reason. Both default to the V1 production values until the API answers.
   */
  const scheduling = ref<SchedulingSettings | null>(null);
  const freezeWindow = ref<FreezeWindowSettings | null>(null);

  // The now-playing response carries the station's zone on every poll, so it is the
  // primary source; the settings read is a fallback for the seconds before the first one.
  const timezone = computed(
    () => nowPlaying.value?.station.timezone ?? scheduling.value?.timezone ?? 'Europe/Amsterdam',
  );

  watch(timezone, (zone) => setStationTimeZone(zone), { immediate: true });
  const freezeMinutesBeforeAir = computed(
    () => freezeWindow.value?.freeze_minutes_before_air ?? 30,
  );

  let nowPlayingTimer: ReturnType<typeof setInterval> | undefined;
  let healthTimer: ReturnType<typeof setInterval> | undefined;
  let channel: RealtimeChannel | null = null;
  /** Whether events are arriving. Exposed so a diagnostics view can say which it is. */
  const live = ref(false);

  const mode = computed(() => nowPlaying.value?.mode ?? 'OFFLINE');

  /** Components whose state needs an operator's attention right now. */
  const problems = computed(() =>
    health.value.filter((entry) => entry.state === 'DOWN' || entry.state === 'DEGRADED'),
  );

  async function refreshNowPlaying(): Promise<void> {
    try {
      nowPlaying.value = await request<NowPlayingResponse>('/playout/now-playing');
      stale.value = false;
      lastError.value = null;
    } catch (error) {
      // The stream may well still be on air; the panel simply cannot see it. Saying
      // "offline" here would be a lie, so the state is marked stale instead.
      stale.value = true;
      lastError.value = error instanceof Error ? error.message : 'Unknown error';
    }
  }

  async function refreshHealth(): Promise<void> {
    try {
      const response = await request<{ data: RuntimeStatusEntry[] }>('/system/status');
      health.value = response.data;
      healthStale.value = false;
    } catch {
      // Health is advisory, but a grid that silently stops updating looks current and is
      // not. The flag lets the health page say so.
      healthStale.value = true;
    }
  }

  /** Read once per session: policy changes far less often than what is on air. */
  async function loadPolicy(): Promise<void> {
    try {
      const [broadcast, reliability] = await Promise.all([
        request<{ data: Record<string, unknown> }>('/settings/broadcast'),
        request<{ data: Record<string, unknown> }>('/settings/reliability'),
      ]);
      scheduling.value = (broadcast.data.scheduling as SchedulingSettings | undefined) ?? null;
      freezeWindow.value =
        (reliability.data.freeze_window as FreezeWindowSettings | undefined) ?? null;
    } catch {
      // The defaults above are the V1 production values, so a failure here leaves the
      // panel correct for this station rather than blank.
    }
  }

  /**
   * Set the timers to match whether the socket is carrying events.
   *
   * Called on every connect and disconnect, so a panel that loses its channel is back
   * to the ten-second cadence within a tick rather than sitting silent.
   */
  function pace(): void {
    if (nowPlayingTimer) clearInterval(nowPlayingTimer);
    if (healthTimer) clearInterval(healthTimer);
    const nowPlayingEvery = live.value ? IDLE_POLL_MS : POLL_MS;
    const healthEvery = live.value ? IDLE_POLL_MS : HEALTH_POLL_MS;
    nowPlayingTimer = setInterval(() => void refreshNowPlaying(), nowPlayingEvery);
    healthTimer = setInterval(() => void refreshHealth(), healthEvery);
  }

  function start(): void {
    void refreshNowPlaying();
    void refreshHealth();
    void loadPolicy();
    pace();

    channel ??= new RealtimeChannel({
      onOpen: () => {
        live.value = true;
        // Events that happened while the channel was down were not kept for us, so the
        // only way back to the truth is to ask rather than to assume nothing changed.
        void refreshNowPlaying();
        void refreshHealth();
        pace();
      },
      onClose: () => {
        live.value = false;
        pace();
      },
      onEvent: (type) => {
        // The event says something changed; the API says what it changed to. Applying
        // payloads here would make this a second implementation of state the API already
        // computes, free to drift from it.
        if (ON_AIR_EVENTS.has(type)) void refreshNowPlaying();
        if (HEALTH_EVENTS.has(type)) void refreshHealth();
      },
    });
    channel.start();
  }

  function stop(): void {
    if (nowPlayingTimer) clearInterval(nowPlayingTimer);
    if (healthTimer) clearInterval(healthTimer);
    nowPlayingTimer = undefined;
    healthTimer = undefined;
    channel?.stop();
    channel = null;
    live.value = false;
  }

  return {
    nowPlaying,
    health,
    mode,
    problems,
    stale,
    healthStale,
    live,
    lastError,
    timezone,
    freezeMinutesBeforeAir,
    loadPolicy,
    refreshNowPlaying,
    refreshHealth,
    start,
    stop,
  };
});
